'use client';

import { useTranslations } from 'next-intl';
import { HStack } from '@terra/ui-styled-system';
import { Text } from './text';
import { IconButton } from './icon-button';
import { Tooltip } from './tooltip';
import { ArrowDown01Icon, ArrowUp01Icon } from '@terra/ui-icons';

export type ListNavigationProps = {
  currentIndex: number;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
  entityTitle?: string;
};
export const ListNavigation = ({
  currentIndex,
  total,
  onPrevious,
  onNext,
  entityTitle,
}: ListNavigationProps) => {
  const t = useTranslations('designSystem.listNavigation');
  const resolvedEntityTitle = entityTitle ?? t('defaultEntityTitle');
  const resolvedAriaLabel = t('navigationAriaLabel', {
    entityTitle: resolvedEntityTitle,
  });
  const resolvedPreviousTooltip = t('previousTooltip', {
    entityTitle: resolvedEntityTitle,
  });
  const resolvedNextTooltip = t('nextTooltip', {
    entityTitle: resolvedEntityTitle,
  });
  const isFirst = currentIndex <= 1;
  const isLast = currentIndex >= total;

  return (
    <HStack
      gap={2}
      alignItems="center"
      role="navigation"
      aria-label={resolvedAriaLabel}
    >
      <HStack gap={1} alignItems="baseline">
        <Text fontSize="sm" color="fg.default">
          {currentIndex}
        </Text>
        <Text fontSize="sm" color="fg.subtle">
          {' / '}
          {total}
        </Text>
      </HStack>
      <HStack gap={2} alignItems="center">
        <Tooltip
          title={resolvedPreviousTooltip}
          positioning={{ strategy: 'fixed' }}
        >
          <span style={{ display: 'inline-block' }}>
            <IconButton
              type="button"
              size="sm"
              variant="outline"
              minW={0}
              w="32px"
              h="32px"
              borderRadius="full"
              cursor={isFirst ? 'not-allowed' : 'pointer'}
              opacity={isFirst ? 0.5 : 1}
              onClick={onPrevious}
              disabled={isFirst}
              aria-label={resolvedPreviousTooltip}
            >
              <ArrowUp01Icon size={16} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={resolvedNextTooltip}
          positioning={{ strategy: 'fixed' }}
        >
          <span style={{ display: 'inline-block' }}>
            <IconButton
              type="button"
              size="sm"
              variant="outline"
              minW={0}
              w="32px"
              h="32px"
              borderRadius="full"
              cursor={isLast ? 'not-allowed' : 'pointer'}
              opacity={isLast ? 0.5 : 1}
              onClick={onNext}
              disabled={isLast}
              aria-label={resolvedNextTooltip}
            >
              <ArrowDown01Icon size={16} />
            </IconButton>
          </span>
        </Tooltip>
      </HStack>
    </HStack>
  );
};
